import { FC, useContext, useEffect, useReducer, useState } from 'react';

import { WCircularProgress, WStack } from '../../wrappers/wrapper';
import styles from './comment-list.module.scss';
import { CommentSection } from '../comment-section/comment-section';
import CommentInput from '../../organisms/comment-input/comment-input';
import { TArticle, TComment, TUser } from '../../../models/common';
import useComment from 'custom-hooks/useComment';
import fnService from 'services/function.service';
import ViewProfile from '../view-profile-popover/view-profile-popover';
import helperService from 'services/helper.service';
import { useNavigate } from 'react-router-dom';
import dbService from 'services/database.service';
import { UserContext } from 'contexts/userContext';

interface CommentListProps {
  article: TArticle;
  onCommentedChanged: () => void;
}
const CommentList: FC<CommentListProps> = ({ article, onCommentedChanged }) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const { comments = [], setComments, loading, fetchComments } = useComment(article._id!);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [openViewProfile, setOpenViewProfile] = useState(false);
  const [userId, setUserId] = useState('');

  useEffect(() => {
    fetchComments();
  }, []);

  const handlePostComment = async (message: string, comment?: TComment) => {
    if (!user?._id) return navigate('/login');

    const mainComment = comment?.is_reply_comment ? comment?.reply_to : comment?._id;

    await fnService.postComment(user?._id!, message, article._id!, mainComment, mainComment);
    fetchComments().then((_) => forceUpdate());
    onCommentedChanged();
  };

  const handleLikeComment = async (comment: TComment) => {
    if (!user?._id) return navigate('/login');

    const { likeIndex, liked } = helperService.getCommentIndexAndIsLiked(comment, user);
    liked ? comment.likes?.splice(likeIndex, 1) : (comment.likes as TUser[]).push(user);
    setComments([...comments]);
    await fnService.likeComment(user._id!, comment._id!);
  };

  const handleDeleteComment = async (comment: TComment) => {
    if (!comment._id) return;

    await dbService.removeComment(comment._id);
    comment.replies?.forEach((reply) => dbService.removeComment((reply as TComment)._id!));
    setComments(comments.filter((el) => el._id !== comment._id));
    forceUpdate();
    onCommentedChanged();
  };

  const handleClickUser = (id: string) => {
    setUserId(id);
    setOpenViewProfile(true);
  };

  const getMainComments = () => {
    return comments?.filter((el) => !el.is_reply_comment);
  };

  const getReplyComments = (comment: TComment) => {
    return comments?.filter((el) => el.reply_to === comment._id);
  };

  return (
    <WStack className={styles.container} alignItems="center">
      {loading ? (
        <WCircularProgress size="1rem" className={styles['circular-progress']} />
      ) : (
        <WStack className={styles['comments-list']}>
          {getMainComments()?.map((comment) => (
            <CommentSection
              key={comment._id}
              commentData={comment}
              replyComments={getReplyComments(comment)!}
              onPost={handlePostComment}
              onClickUser={handleClickUser}
              onLike={handleLikeComment}
              onDelete={handleDeleteComment}
            />
          ))}
          <CommentInput onPost={handlePostComment} />
        </WStack>
      )}
      {openViewProfile && (
        <ViewProfile
          user_id={userId}
          onClose={() => setOpenViewProfile(false)}
          showEditButton={true}
        />
      )}
    </WStack>
  );
};

export default CommentList;
